import React, { CSSProperties } from 'react';

interface ShowProps {
  when: boolean;
  keyed?: boolean;
  fallback?: JSX.Element;
  children: JSX.Element | ((item: any) => JSX.Element);
  useDisplayNone?: boolean;
  useVisibility?: boolean;
  useAnimation?: boolean;
  direction?: 'left' | 'right' | 'top' | 'bottom';
  duration?: number;
  timingFunction?: string;
  className?: string;
  distance?: number;
}

const Show: React.FC<ShowProps> = ({
  when,
  keyed,
  fallback,
  children,
  useDisplayNone = false,
  useVisibility = false,
  useAnimation = false,
  direction = 'right',
  duration = 300,
  timingFunction = 'ease',
  className = '',
  distance = 100,
}) => {
  if (useDisplayNone || useVisibility) {
    const style = useDisplayNone ? { display: when ? 'block' : 'none' } : { visibility: when ? 'visible' : 'hidden' };
    return (
      <div className={className} style={style as CSSProperties}>
        {typeof children === 'function' ? children({}) : children}
      </div>
    );
  }

  if (useAnimation) {
    const getAnimationStyle = (): CSSProperties => {
      const baseStyle: CSSProperties = {
        transition: `all ${duration}ms ${timingFunction}`,
        transform: when ? 'translate(0, 0)' : '',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        position: 'relative',
        opacity: when ? 1 : 0,
        pointerEvents: when ? 'auto' : 'none',
      };

      const translations = {
        left: `translate(-${distance}%, 0)`,
        right: `translate(${distance}%, 0)`,
        top: `translate(0, -${distance}%)`,
        bottom: `translate(0, ${distance}%)`,
      };

      if (!when) {
        baseStyle.transform = translations[direction];
      }

      return baseStyle;
    };

    return (
      <div className={className} style={getAnimationStyle()}>
        {typeof children === 'function' ? children({}) : children}
      </div>
    );
  }

  return when ? (typeof children === 'function' ? children({}) : children) : fallback;
};

export default Show;
